import { IngredientsContext } from "@/app/clientLayout";
import {
  DefaultIngredientMarker,
  InformationCircleComponent,
  IngredientNote,
} from "@/components/Commons";
import CreateIngredients from "@/components/CreateIngredients";
import GetImageURL from "@/components/GetImageURL";
import IngredientsGridItem from "@/components/IngredientsGridItem";
import { CurrentUser, UserContext } from "@/components/Page";
import TextEditor from "@/components/TextEditor";
import { InventoryGrid } from "@/components/styles/Layout";
import Tooltip from "@/components/styles/Tooltip";
import {
  ShapedClass,
  dot,
  searchClasses,
  shapeClass,
  shapeClassLinear,
} from "@/lib/class-utilities";
import {
  MEASUREMENT_ID_WEIGHT,
  combineUnits,
  getBaseUnit,
  getUnitOptions,
  stringIsValidNumber,
  stringToNumber,
  toAbsoluteAmount,
  unitToOption,
} from "@/lib/measurement";
import useIndexedDB from "@/lib/useIndexedDB";
import {
  TagOptionType,
  classNames,
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  searchTags,
} from "@/lib/util";
import {
  AncillariesComponent,
  CreateManyIngredientsMutation,
  RecipeSectionCreateWithoutRecipeInput,
  Recipe_MadeInGroups,
  useCreateManyRecipesMutation,
  useOrderCarouselQuery,
} from "@/queries/autogenerate/graphql";
import {
  AcademicCapIcon,
  ArrowPathIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  PencilIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import cuid from "cuid";
import Link from "next/link";
import React, { useContext, useRef, useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { compareTwoStrings } from "string-similarity";
import styled from "styled-components";
import { AsyncSelectIngredient } from "../AsyncSelectIngredient";
import DialogComponent from "../Dialog";
import DragNDrop, { SortableColumn, SortableItem } from "../DragNDrop";
import ErrorMessage from "../ErrorMessage";
import { FolderSelectInput } from "../RecipesPage/util";
import DragIndicatorIcon from "../svg/DragIndicatorIcon";
import { CreateManyRecipesFnType } from "./FinalisePartialRecipe";
import { inlineConversion } from "./util";

const Input = styled.input`
  padding: 5px;
  min-height: 38px;
  width: ${(props) => props.width || "400px"};
  border: 1px solid hsl(0, 0%, 80%);
  border-radius: 4px;
  text-align: center;
`;

interface SimilarRecipesProps {
  schoolID: string;
  query: string;
}

export const SimilarRecipes: React.FC<SimilarRecipesProps> = ({
  schoolID,
  query = "",
}) => {
  const [{ fetching, error, data }] = useOrderCarouselQuery({
    variables: { schoolID },
  });

  if (error) return null;
  if (fetching) return <p>...</p>;
  if (!data) return <p>No Data! (312)</p>;

  const { recipes } = data;

  const compare: (a: string, b: string) => number = (a = "", b = "") =>
    compareTwoStrings(a.toLowerCase(), b.toLowerCase());

  const similarRecipes = recipes
    .filter(({ name }) => compare(name, query) >= 0.5)
    .filter((_, i) => i < 5)
    .map((i) => ({
      ...i,
      similarity: Math.round(compare(i.name, query) * 100),
    }))
    .sort((a, b) => b.similarity - a.similarity);

  if (!similarRecipes.length) return null;

  return (
    <div className="min-w-[118] rounded border border-solid border-green-500 bg-green-100 bg-opacity-25 p-2">
      <span className="mb-4 text-center font-normal">
        To avoid creating duplicate recipes, here is a list of similar recipes
        in your collection:
      </span>
      <ul>
        {similarRecipes.map(
          ({ id, name, tags, classes, recipeCollection, similarity }) => (
            <li key={id} className="my-1.5">
              {name} - {similarity}%{" "}
              {classes.length
                ? classes.map((c) => (
                    <span key={c.id} className="tag tag-cyan">
                      <AcademicCapIcon className="h-4" />
                      {c.name}
                    </span>
                  ))
                : null}
              {tags.length
                ? tags.map((tag) => (
                    <span key={tag.id} className="tag">
                      <TagIcon className="h-4" />
                      {tag.name}
                    </span>
                  ))
                : null}
              {recipeCollection ? (
                <span className="tag tag-blue">
                  <FolderIcon className="h-4" /> {recipeCollection.name}
                </span>
              ) : null}
            </li>
          )
        )}
      </ul>
    </div>
  );
};

interface CreateRecipesProps {
  submitRecipe: (mutation: CreateManyRecipesFnType) => void;
}

const CreateRecipes: React.FC<CreateRecipesProps> = ({ submitRecipe }) => {
  const [{ fetching, error }, createManyRecipes] =
    useCreateManyRecipesMutation();

  if (error) return <ErrorMessage error={error} />;

  return (
    <button
      className="btn btn-primary"
      onClick={() => submitRecipe(createManyRecipes)}
      disabled={fetching}
    >
      {fetching ? <div className="spinner"></div> : null}
      Create Recipe
    </button>
  );
};

type RecipeIngredient = {
  uuid: string;
  id: string;
  amount: number | string;
  preparation: string;
  unitId: string;
};

export interface RecipeIngredientSection {
  uuid: string;
  name: string;
  section: boolean;
  ingredients: RecipeIngredient[];
}

// A function to transform an array of sections into an array of ingredients
const getRecipeIngredients = (
  recipeIngredients: RecipeIngredientSection[]
): RecipeIngredient[] =>
  recipeIngredients.reduce((acc, ris) => {
    return [...acc, ...ris.ingredients];
  }, []);

interface DraggableLocation {
  droppableId: string;
  index: number;
}

interface ListedRecipeIngredientsProps {
  user: CurrentUser;
  recipeIngredientSections: RecipeIngredientSection[];
  setRecipeIngredientSections: (newValue: RecipeIngredientSection[]) => void;
}

const ListedRecipeIngredients: React.FC<ListedRecipeIngredientsProps> = ({
  user,
  recipeIngredientSections,
  setRecipeIngredientSections,
}) => {
  const { ingredients: defaultIngredients } = useContext(IngredientsContext);

  const addSection = () => {
    const sectionUUID = cuid();

    setRecipeIngredientSections([
      ...recipeIngredientSections,
      {
        uuid: sectionUUID,
        name: `Section ${recipeIngredientSections.length}`,
        section: true,
        ingredients: [],
      },
    ]);
  };

  const updateSectionName = (uuid: string, name: string) => {
    setRecipeIngredientSections(
      recipeIngredientSections.map((ris) =>
        ris.uuid === uuid ? { ...ris, name } : ris
      )
    );
  };

  const removeSection = (uuid: string) => {
    const oldSection = recipeIngredientSections.find(
      (ris) => ris.uuid === uuid
    );
    if (!oldSection) return;

    setRecipeIngredientSections(
      recipeIngredientSections
        .map((ris, index) => {
          if (index != 0) return ris;

          return {
            ...ris,
            ingredients: [...ris.ingredients, ...oldSection.ingredients],
          };
        })
        .filter((ris) => ris.uuid !== uuid)
    );
  };

  const updateRecipeIngredient = (
    sectionUUID: string,
    ingredientUUID: string,
    data: Partial<RecipeIngredient>
  ) => {
    const newRecipeIngredientSections = recipeIngredientSections.map((ris) => {
      if (ris.uuid !== sectionUUID) return ris;

      return {
        ...ris,
        ingredients: ris.ingredients.map((ri) => {
          if (ri.uuid !== ingredientUUID) return ri;

          return {
            ...ri,
            ...data,
          };
        }),
      };
    });

    setRecipeIngredientSections(newRecipeIngredientSections);
  };

  const removeRecipeIngredient = (
    sectionUUID: string,
    ingredientUUID: string
  ) => {
    const newRecipeIngredientSections = recipeIngredientSections.map((ris) => {
      if (ris.uuid !== sectionUUID) return ris;

      return {
        ...ris,
        ingredients: ris.ingredients.filter((ri) => ri.uuid !== ingredientUUID),
      };
    });

    setRecipeIngredientSections(newRecipeIngredientSections);
  };

  const old = false;

  return !old ? (
    <DragNDrop
      collection={recipeIngredientSections}
      itemKey="ingredients"
      setCollection={setRecipeIngredientSections}
    >
      {(recipeIngredientSections) => (
        <div className="w-full max-w-md">
          <h3>Ingredients</h3>
          <div>
            {recipeIngredientSections.map((ris, columnIndex) => {
              if (!ris) return null;

              return (
                <SortableColumn
                  key={ris.uuid}
                  id={ris.uuid}
                  index={columnIndex}
                >
                  {({ ref, handleRef, isDropTarget }) => (
                    <div ref={ref}>
                      <div className="rounded py-1.5">
                        {ris.section ? (
                          <div className="group -ml-3 flex items-center gap-2.5 py-5">
                            <div
                              ref={handleRef}
                              title="Sort Sections"
                              className="flex items-center justify-center"
                            >
                              <DragIndicatorIcon className="h-6" />
                            </div>
                            <input
                              type="text"
                              value={ris.name}
                              onChange={(e) =>
                                updateSectionName(ris.uuid, e.target.value)
                              }
                              onFocus={(e) => e.target.select()}
                              className="rounded border border-solid border-gray-300 p-1.5"
                              autoFocus
                            />
                            <hr className="flex-1 border-gray-400" />
                            <Tooltip content="Remove Section">
                              <button
                                onClick={() => removeSection(ris.uuid)}
                                className="opacity-0 transition group-hover:opacity-100"
                              >
                                <TrashIcon className="icon icon-orange" />
                              </button>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="group -ml-3 flex items-center gap-2.5 py-5">
                            <div
                              ref={handleRef}
                              title="Sort Ingredients"
                              className="flex items-center justify-center"
                            >
                              <DragIndicatorIcon className="h-6" />
                            </div>
                            <h3 className="font-medium text-gray-500/30">
                              Default Section
                            </h3>
                          </div>
                        )}
                        {ris.ingredients.filter((i) =>
                          defaultIngredients.find((di) => di.id === i.id)
                        ).length ? (
                          <div className="space-y-5">
                            {ris.ingredients
                              .filter((i) =>
                                defaultIngredients.find((di) => di.id === i?.id)
                              )
                              .map(
                                (recipeIngredient: RecipeIngredient, index) => (
                                  <IndividualRecipeIngredient
                                    key={recipeIngredient.uuid}
                                    {...{
                                      user,
                                      index,
                                      recipeIngredient,
                                      ris,
                                      updateRecipeIngredient,
                                      removeRecipeIngredient,
                                    }}
                                  />
                                )
                              )}
                          </div>
                        ) : (
                          <>
                            {!ris.name || isDropTarget ? null : (
                              <div className="rounded border border-dashed border-gray-300 bg-gradient-to-tr from-cyan-50 to-gray-50 p-2 text-center">
                                <h3>Empty Section</h3>
                                <small>
                                  Add ingredients to this section by dragging
                                  them.
                                </small>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </SortableColumn>
              );
            })}
          </div>

          <div className="-ml-3 flex items-center gap-2.5 py-5">
            <button className="btn group text-lg" onClick={() => addSection()}>
              <PlusCircleIcon className="icon icon-gray ml-0 group-hover:text-gray-900" />
              Add an Ingredient Section
            </button>
            <hr className="flex-1 border-gray-400" />
          </div>
        </div>
      )}
    </DragNDrop>
  ) : null;
};

interface IndividualRecipeIngredientProps {
  user: CurrentUser;
  index: number;
  recipeIngredient: RecipeIngredient;
  ris: RecipeIngredientSection;
  updateRecipeIngredient: (
    sectionUUID: string,
    ingredientUUID: string,
    data: Partial<RecipeIngredient>
  ) => void;
  removeRecipeIngredient: (sectionUUID: string, ingredientUUID: string) => void;
}

const IndividualRecipeIngredient: React.FC<IndividualRecipeIngredientProps> = ({
  user,
  index,
  recipeIngredient: { uuid, id, amount, preparation, unitId },
  ris,
  updateRecipeIngredient,
  removeRecipeIngredient,
}) => {
  const { ingredients: defaultIngredients } = useContext(IngredientsContext);

  const [editDialog, setEditDialog] = useState(false);
  const openEdit = () => setEditDialog(true);
  const closeEdit = () => setEditDialog(false);

  const tempIngredient = defaultIngredients.find((di) => di.id == id);
  if (!tempIngredient) return null;

  const { isDefault, name, note, units, density, measurement } = tempIngredient;

  const combinedUnits = combineUnits({
    measurement,
    units,
    density,
  });

  // Check if the unit still exists on the ingredient
  const unit = combinedUnits.find((cu) => cu.id === unitId);

  return (
    <SortableItem id={uuid} index={index} column={ris.uuid}>
      {({ ref, handleRef, isDragging }) => (
        <div
          className="group relative flex rounded bg-gray-200 shadow-md"
          ref={ref}
        >
          <div
            ref={handleRef}
            title="Sort Ingredients"
            className="flex items-center justify-center"
          >
            <DragIndicatorIcon className="h-9" />
          </div>
          <div className="group flex-1 space-y-2.5 p-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center">
                {isDefault ? <DefaultIngredientMarker /> : null}
                {name}
                {note ? <IngredientNote note={note} /> : null}
                <Tooltip content="Edit Ingredient">
                  <button
                    onClick={() => openEdit()}
                    className="opacity-0 transition group-hover:opacity-100"
                  >
                    <PencilIcon className="icon icon-gray " />
                  </button>
                </Tooltip>
                <DialogComponent
                  isOpen={editDialog}
                  onClose={closeEdit}
                  aria-label="Edit ingredient"
                >
                  <h2 className="mb-8">Edit {name}</h2>
                  <InventoryGrid itemHeight="90px" itemWidth="400px">
                    <IngredientsGridItem
                      user={user}
                      ingredient={tempIngredient}
                      isFragment={true}
                    />
                  </InventoryGrid>
                </DialogComponent>
              </span>
              <Tooltip content="Remove Ingredient">
                <button
                  onClick={() => {
                    removeRecipeIngredient(ris.uuid, uuid);
                  }}
                  className="opacity-0 transition group-hover:opacity-100"
                >
                  <TrashIcon className="icon icon-orange" />
                </button>
              </Tooltip>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="relative">
                <input
                  type="text"
                  name={"amount" + uuid}
                  id={"amount" + uuid}
                  onChange={(e) => {
                    const { value: newAmount } = e.target;

                    updateRecipeIngredient(ris.uuid, uuid, {
                      amount: newAmount,
                    });
                  }}
                  value={amount ?? ""}
                  required={true}
                  autoFocus
                  className={classNames(
                    "w-22 rounded border border-solid p-1.5",
                    stringIsValidNumber(String(amount))
                      ? "border-gray-400"
                      : "border-orange-400 outline-orange-600"
                  )}
                />
                {stringIsValidNumber(String(amount)) ? null : (
                  <Tooltip content={`"${amount}" is not a valid input`}>
                    <span>
                      <ExclamationTriangleIcon className="icon icon-orange absolute right-0 top-0 mt-3.5" />
                    </span>
                  </Tooltip>
                )}
              </div>
              <Select
                className="radio-select flex-1"
                classNamePrefix="react-select"
                value={unit ? unitToOption(unit) : null}
                options={getUnitOptions(
                  combineUnits({
                    measurement,
                    units,
                    density,
                  }),
                  density ?? undefined
                )}
                noOptionsMessage={() => "No Options"}
                onChange={(data) => {
                  updateRecipeIngredient(ris.uuid, uuid, {
                    unitId: data?.value,
                  });
                }}
              />
              {!unit ? (
                <Tooltip
                  content={
                    "The selected unit does not exist on this ingredient. Please change the unit."
                  }
                >
                  <span>
                    <ExclamationTriangleIcon className="icon icon-orange" />
                  </span>
                </Tooltip>
              ) : null}
              <input
                type="text"
                name={"preparation" + uuid}
                id={"preparation" + uuid}
                className="w-32 rounded border border-solid border-gray-400 p-1.5"
                placeholder="grated, sliced..."
                value={preparation}
                onChange={(e) => {
                  updateRecipeIngredient(ris.uuid, uuid, {
                    preparation: e.target.value || "",
                  });
                }}
              />
            </div>
            {(typeof amount === "string" && amount.includes("/")) ||
            (unit &&
              ((measurement?.id === MEASUREMENT_ID_WEIGHT && density) ||
                (!unit.measurementId && measurement))) ? (
              <div className="flex items-center gap-1.5">
                {unit &&
                ((measurement?.id === MEASUREMENT_ID_WEIGHT && density) ||
                  (!unit.measurementId && measurement)) ? (
                  <div className="pl-1.5 text-sm">
                    {inlineConversion(
                      amount,
                      unit,
                      combinedUnits,
                      density ?? 0
                    )}
                    <Tooltip
                      content="Conversions are shown inline for a quick reference check. If the conversion is incorrect, this means the density is incorrect and should be updated."
                      placement="bottom"
                    >
                      <span>
                        <InformationCircleComponent />
                      </span>
                    </Tooltip>
                  </div>
                ) : (
                  <span className="text-sm">
                    {stringToNumber(String(amount))}
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </SortableItem>
  );
};

interface SharedRecipeInfoProps {}

export const SharedRecipeInfo: React.FC<SharedRecipeInfoProps> = () => {
  return (
    <Tooltip content="Shared recipes will be visible to other schools from the 'discover' page, and can also be used as a template when creating recipes.">
      <span>
        <InformationCircleComponent />
      </span>
    </Tooltip>
  );
};

interface Props {
  user: CurrentUser;
  setRecipeNameFn?: (name: string) => void;
}

const CreateRecipeManually: React.FC<Props> = ({ user, setRecipeNameFn }) => {
  const {
    ingredients: defaultIngredients,
    setIngredients,
    isLoading,
  } = useContext(IngredientsContext);

  const [recipeName, setRecipeName] = useIndexedDB(
    "manualRecipeName",
    "",
    !user.saveRecipeProgress
  );
  const [isPublic, setPublic] = useIndexedDB(
    "manualRecipePublic",
    false,
    !user.saveRecipeProgress
  );
  const [servings, setServings] = useIndexedDB<number | null>(
    "manualRecipeServings",
    null,
    !user.saveRecipeProgress
  );
  const [madeInGroupsValue, setMadeInGroupsValue] =
    useIndexedDB<Recipe_MadeInGroups | null>(
      "manualMadeInGroups",
      null,
      !user.saveRecipeProgress
    );
  const [method, setMethod] = useIndexedDB(
    "manualRecipeMethod",
    "",
    !user.saveRecipeProgress
  );
  const [image, setImage] = useIndexedDB(
    "manualRecipeImage",
    "",
    !user.saveRecipeProgress
  );
  const [info, setInfo] = useIndexedDB<string[]>(
    "manualRecipeInfo",
    [],
    !user.saveRecipeProgress
  );
  const [recipeClassId, setRecipeClassId] = useIndexedDB<string | null>(
    "manualRecipeClasses",
    null,
    !user.saveRecipeProgress
  );
  const recipeClass: ShapedClass | null = recipeClassId
    ? user.school?.classes
        .map(shapeClassLinear)
        .find((c) => c.obj.id == recipeClassId) ?? null
    : null;

  const [recipeTags, setRecipeTags] = useIndexedDB<readonly TagOptionType[]>(
    "manualRecipeTags",
    [],
    !user.saveRecipeProgress
  );
  const [folder, setFolder] = useIndexedDB(
    "manualRecipeFolder",
    "",
    !user.saveRecipeProgress
  );
  const [recipeIngredientSections, setRecipeIngredientSections] = useIndexedDB<
    RecipeIngredientSection[]
  >(
    "manualRecipeIngredients4",
    [
      {
        uuid: cuid(),
        name: "",
        section: false,
        ingredients: [],
      },
    ],
    !user.saveRecipeProgress
  );

  const [isCreatingIngredients, setCreatingIngredients] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  const titleInput = useRef<HTMLInputElement>(null);

  const ingredientInput = useRef<HTMLInputElement>(null);

  const reset = () => {
    setRecipeName("");
    if (setRecipeNameFn) {
      setRecipeNameFn("");
    }
    setPublic(false);
    setServings(null);
    setMadeInGroupsValue(null);
    setRecipeTags([]);
    setImage("");
    setMethod("");
    setRecipeIngredientSections([
      {
        uuid: cuid(),
        name: "",
        section: false,
        ingredients: [],
      },
    ]);
    setInfo([]);
    setCreatingIngredients(false);

    if (titleInput.current != document.activeElement) {
      titleInput.current?.focus();
    }
  };

  const onCompleted: (recipeID: string) => void = (recipeID) => {
    // toast, with a link to the recipe if only one recipe was created
    toast.success((t) => (
      <div>
        <p>
          The recipe &quot;{recipeName}&quot; has been created. You can view and
          edit it from the recipes page
        </p>
        <Link
          prefetch={false}
          href={`/recipes?folder=${folder || ""}#${recipeID}`}
          className="btn btn-sm btn-subtle"
          onClick={() => {
            toast.dismiss(t.id);
          }}
        >
          View Recipe
        </Link>
      </div>
    ));

    reset();
  };

  const unsortedClasses =
    isAdmin(user) || isAssistant(user)
      ? user.school?.classes
      : user.school?.classes.filter((c) =>
          c.users.some((u) => u.id === user.id)
        );

  const folders = user.school?.recipeCollections ?? [];

  const submitRecipe: (mutation: CreateManyRecipesFnType) => void = (
    mutation
  ) => {
    if (!user.school) return;
    const { requiresTaggedRecipes: requiresTags } = user.school;

    if (
      getRecipeIngredients(recipeIngredientSections)
        .filter((i) => defaultIngredients.find((di) => di.id === i.id))
        .some((ri) => !stringIsValidNumber(String(ri.amount)))
    ) {
      toast.error(
        (t) => (
          <div className="space-y-3">
            <p className="font-medium">Ingredient amounts are invalid for:</p>
            <ul className="list-inside list-disc">
              {getRecipeIngredients(recipeIngredientSections)
                .filter((i) => defaultIngredients.find((di) => di.id === i.id))
                .filter((ri) => !stringIsValidNumber(String(ri.amount)))
                .map((ri) => (
                  <li key={ri.id}>
                    {defaultIngredients.find((ing) => ing.id == ri.id)?.name ??
                      "Unknown Ingredient"}
                  </li>
                ))}
            </ul>
            <button className="btn mx-0" onClick={() => toast.dismiss(t.id)}>
              Dismiss
            </button>
          </div>
        ),
        {
          duration: 30 * 1000,
        }
      );
      return;
    } else if (
      recipeIngredientSections.some((ris) =>
        ris.ingredients
          .filter((i) => defaultIngredients.find((di) => di.id === i.id))
          .some(({ id, unitId }) => {
            const tempIngredient = defaultIngredients.find((di) => di.id == id);
            if (!tempIngredient) return false;

            const { units, density, measurement } = tempIngredient;

            return !combineUnits({
              measurement,
              units,
              density,
            }).find((cu) => cu.id === unitId);
          })
      )
    ) {
      toast.error(
        "Some ingredients have non-existent units. Please change the units.",
        { icon: <ExclamationTriangleIcon className="icon-lg icon-orange" /> }
      );
      return;
    } else if (requiresTags && !recipeTags.length) {
      toast.error("Recipes must be given a tag");
      return;
    } else if (isPublic && !method) {
      toast.error(
        "Recipes cannot be shared without a method. Please provide a method."
      );
      return;
    }

    const recipeId = cuid();

    let ingredientPosition = 1;

    // Boolean to know which sections is... sectionless
    const sections: [boolean, RecipeSectionCreateWithoutRecipeInput][] =
      recipeIngredientSections.map<
        [boolean, RecipeSectionCreateWithoutRecipeInput]
      >(({ uuid, name, section, ingredients }, index) => [
        section,
        {
          id: uuid,
          name,
          position: index + 1,
          recipeIngredients: {
            create: ingredients
              .filter((i) => defaultIngredients.find((di) => di.id === i.id))
              .map(({ uuid, id, amount, unitId, preparation }) => {
                const tempIngredient = defaultIngredients.find(
                  (ing) => ing.id == id
                );
                if (!tempIngredient) return null;

                const tempUnit = combineUnits(tempIngredient).find(
                  (cu) => cu.id === unitId
                );
                if (!tempUnit) return null;

                return {
                  id: uuid,
                  recipe: { connect: { id: recipeId } },
                  position: ++ingredientPosition,
                  amount: toAbsoluteAmount(
                    stringToNumber(String(amount)),
                    tempUnit,
                    tempIngredient.density ?? undefined,
                    servings ?? undefined
                  ),
                  preparation,
                  ingredient: {
                    connect: { id },
                  },
                  unit: { connect: { id: unitId } },
                };
              })
              .filter((ri): ri is NonNullable<typeof ri> => ri !== null),
          },
        },
      ]);

    console.log("sections", sections);

    mutation({
      schoolID: user.school.id,
      recipes: [
        {
          id: recipeId,
          name: recipeName,
          public: isPublic,
          process: "manual",
          servings: servings || 1,
          madeInGroups: madeInGroupsValue || undefined,
          image: image || undefined,
          method: method || undefined,
          recipeInformation: {
            create: info.map((contents) => ({ contents })),
          },
          recipeIngredients: {
            create: sections
              .find(([isSection]) => !isSection)?.[1]
              ?.recipeIngredients?.create?.map((ri) => ({
                ...ri,
                recipe: undefined,
              })),
          },
          recipeSections: {
            create: sections
              .filter(([isSection]) => isSection)
              .map(([, section]) => section),
          },
          user: { connect: { id: user.id } },
          tags: {
            connect: recipeTags
              .filter((t) => !t.__isNew__)
              .map((t) => ({ id: t.value })),
            create: recipeTags
              .filter((t) => t.__isNew__)
              .map((t) => ({
                id: t.value,
                name: t.label,
                school: { connect: { id: user.school?.id } },
              })),
          },
          classes: recipeClass
            ? {
                connect: [{ id: recipeClass.obj.id }],
              }
            : undefined,
          recipeCollection:
            folder && folders.some((f) => f.id === folder)
              ? { connect: { id: folder } }
              : undefined,
        },
      ],
    }).then(({ error }) => {
      if (error && !error.message.includes("[Network] Minfied Error #19")) {
        toast.error("There was an error creating the recipe: " + error.message);
        console.error(error);
      } else {
        onCompleted(recipeId);
      }
    });
  };

  return (
    <>
      <div
        className={classNames(
          "flex w-full flex-wrap justify-around",
          recipeIngredientSections.some((ris) =>
            ris.ingredients.some((i) =>
              defaultIngredients.some((di) => di.id === i.id)
            )
          )
            ? "flex-row items-end"
            : "flex-col items-center"
        )}
      >
        <div className="m-3" style={{ width: "400px" }}>
          <div
            className={`overflow-hidden transition-all ${
              recipeName ? "h-12 opacity-100" : "h-0 opacity-0"
            }`}
          >
            <button className="btn" onClick={reset}>
              Reset
            </button>
          </div>

          <div className="my-5">
            <label htmlFor="name">
              <h2 className="mb-5 flex items-center justify-center">
                <PencilSquareIcon className="mr-2 w-5 text-gray-600" /> Manually
                Create a Recipe
              </h2>
            </label>
            <input
              type="text"
              name="name"
              placeholder="Start With a Name"
              className="w-full rounded border border-solid border-gray-300 p-2.5 text-2xl transition hover:border-gray-400"
              ref={titleInput}
              autoFocus
              onChange={(e) => {
                setRecipeName(e.target.value);
                if (setRecipeNameFn) {
                  setRecipeNameFn(e.target.value);
                }
              }}
              value={recipeName}
              required
            />
            {recipeName ? (
              <div className="my-3 flex items-center justify-center">
                <SimilarRecipes
                  schoolID={user.school?.id || ""}
                  query={recipeName}
                />
              </div>
            ) : null}
          </div>
          {recipeName ? (
            <>
              {isTeacher(user) ? (
                <label htmlFor="publicRecipe" className="my-5">
                  <input
                    type="checkbox"
                    name="publicRecipe"
                    id="publicRecipe"
                    checked={isPublic}
                    onChange={(e) => setPublic(e.target.checked)}
                    className="m-2"
                  />
                  Shared
                  <SharedRecipeInfo />
                </label>
              ) : null}
              <div className="my-5 flex w-full items-center justify-between">
                <label htmlFor="servings">
                  How many servings will it produce?
                </label>
                <Input
                  width="100px"
                  type="number"
                  min={1}
                  name="servings"
                  placeholder="0"
                  onChange={(e) => setServings(parseFloat(e.target.value))}
                  value={servings || ""}
                  required
                />
              </div>
              {servings ? (
                <>
                  <div className="my-5 w-full">
                    <div className="flex items-center justify-between">
                      <span>Will it be made in groups? (optional)</span>
                      <select
                        name="madeInGroups"
                        id="madeIngroups"
                        value={madeInGroupsValue || ""}
                        onChange={(e) => {
                          setMadeInGroupsValue(
                            (e.target.value as Recipe_MadeInGroups) || null
                          );
                        }}
                        className="rounded border border-solid border-gray-300 p-1.5"
                      >
                        <option value="">(select an option)</option>
                        <option value={Recipe_MadeInGroups.Individuals}>
                          individually
                        </option>
                        <option value={Recipe_MadeInGroups.Pairs}>
                          in pairs
                        </option>
                        <option value={Recipe_MadeInGroups.Trios}>
                          in groups of three
                        </option>
                        <option value={Recipe_MadeInGroups.Four}>
                          in groups of four
                        </option>
                        <option value={Recipe_MadeInGroups.Five}>
                          in groups of five
                        </option>
                        <option value={Recipe_MadeInGroups.Six}>
                          in groups of six
                        </option>
                      </select>
                    </div>
                    <small className="mt-1 block max-w-md rounded border border-solid border-blue-200 bg-blue-50 p-1.5 font-normal text-blue-600">
                      Note: This does not affect orders, it is only for
                      informational purposes. If you want to change how much
                      this recipe produces, update the servings or the
                      ingredients themselves.
                    </small>
                  </div>
                  <div className="my-5">
                    <label>Class (Optional)</label>
                    <AsyncSelect
                      className="radio-select"
                      classNamePrefix="react-select"
                      isClearable={true}
                      isSearchable={true}
                      value={recipeClass}
                      // defaultValue={unsortedClasses.map(shapeClassLinear).filter((c) => !recipe.classes.some((rc) => rc.id == c.obj.id))[0] || null}
                      defaultOptions
                      loadOptions={(inputValue, callback) => {
                        callback(
                          searchClasses(
                            inputValue,
                            unsortedClasses?.map(shapeClass) ?? []
                          )
                        );
                      }}
                      noOptionsMessage={() => "No Options"}
                      maxMenuHeight={200}
                      placeholder="Select a Class to Connect"
                      onChange={(data) => {
                        if (data?.value) {
                          setRecipeClassId(data.obj.id);
                        } else {
                          setRecipeClassId(null);
                        }
                      }}
                      // onChange={(newValue) => setNewClass(newValue || null)}
                      styles={{
                        container: (provided) => ({
                          ...provided,
                          position: "sticky",
                          top: "0px",
                          marginTop: "0",
                          zIndex: 20,
                          width: "400px",
                        }),
                        control: (styles) => ({
                          ...styles,
                          backgroundColor: "white",
                        }),
                        option: (styles, { data }) => ({
                          ...styles,
                          ...dot(...data.colors),
                        }),
                        input: (styles) => ({
                          ...styles,
                          ...dot(...(recipeClass?.colors || [])),
                        }),
                        placeholder: (styles) => ({ ...styles, ...dot() }),
                        singleValue: (provided, { data }) => ({
                          ...provided,
                          ...dot(...data.colors),
                        }),
                      }}
                    />
                  </div>
                  <div className="my-5">
                    <label>
                      Tags
                      {user.school?.requiresTaggedRecipes ? "" : " (optional)"}
                    </label>
                    <AncillariesComponent
                      variables={{ schoolID: user.school?.id || "" }}
                    >
                      {({ error, fetching, data }) =>
                        error ? (
                          <ErrorMessage error={error} />
                        ) : fetching ? (
                          <div className="spinner" />
                        ) : !isStudent(user) || user.school?.permCreateTags ? (
                          <AsyncCreatableSelect
                            id="tags"
                            placeholder="Search or Create"
                            isClearable
                            isMulti
                            isSearchable
                            defaultOptions
                            loadOptions={(inputValue, callback) => {
                              callback(
                                searchTags(inputValue, data?.tags ?? [])
                              );
                            }}
                            noOptionsMessage={() => "No Options"}
                            value={recipeTags}
                            onCreateOption={(inputValue) => {
                              const newTag = {
                                value: cuid(),
                                label: inputValue,
                                __isNew__: true,
                                type: "tag",
                              } as TagOptionType;
                              setRecipeTags([...recipeTags, newTag]);
                            }}
                            onChange={(value) => setRecipeTags(value || [])}
                            maxMenuHeight={250}
                            styles={{
                              container: (provided) => ({
                                ...provided,
                                width: "400px",
                              }),
                            }}
                          />
                        ) : (
                          <AsyncSelect
                            id="tags"
                            placeholder="Search..."
                            isClearable
                            isMulti
                            isSearchable
                            value={recipeTags}
                            defaultOptions
                            loadOptions={(inputValue, callback) => {
                              callback(
                                searchTags(inputValue, data?.tags ?? [])
                              );
                            }}
                            noOptionsMessage={() => "No Options"}
                            onChange={(value) => setRecipeTags(value || [])}
                            maxMenuHeight={250}
                            styles={{
                              container: (provided) => ({
                                ...provided,
                                width: "400px",
                              }),
                            }}
                          />
                        )
                      }
                    </AncillariesComponent>
                  </div>
                  <div className="my-5">
                    <label>Folder</label>
                    <FolderSelectInput
                      sortedFolders={folders}
                      showAll={true}
                      //
                      style={{ width: "400px" }}
                      value={folder}
                      onChange={(e) => setFolder(e.target.value)}
                    />
                  </div>
                  {!user.school?.requiresTaggedRecipes || recipeTags.length ? (
                    <>
                      <div className="my-5">
                        {image ? (
                          <div className="relative mx-auto w-max">
                            <img
                              src={image}
                              alt={`Image of ${recipeName}`}
                              className="mx-auto my-5 block rounded-lg"
                              style={{
                                maxWidth: "300px",
                                maxHeight: "300px",
                                minWidth: "150px",
                                minHeight: "150px",
                                boxShadow: "#a0a0a0 0px 0px 5px",
                              }}
                            />
                            <Tooltip content="Replace Image">
                              <button
                                className="btn absolute bottom-0 left-0"
                                onClick={open}
                              >
                                <ArrowPathIcon className="w-6" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Remove Image">
                              <button
                                className="btn btn-warning absolute bottom-0 right-0"
                                onClick={() => setImage("")}
                              >
                                <TrashIcon className="w-6" />
                              </button>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="relative mx-auto w-max rounded-lg border border-solid border-gray-400 bg-gradient-to-tr from-cyan-100 to-green-100 px-14 py-14 text-center">
                            <CameraIcon className="w-18 text-gray-400" />
                            <p>No Image</p>
                            <Tooltip content="Add an Image">
                              <button
                                className="btn btn-primary absolute bottom-0 right-0"
                                onClick={open}
                              >
                                <PlusIcon className="w-6" />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                        <DialogComponent
                          isOpen={showDialog}
                          onClose={close}
                          aria-label="work in progress"
                        >
                          <GetImageURL
                            onCompleted={(url) => {
                              close();

                              setImage(url);
                            }}
                          />
                          <button className="btn" onClick={close}>
                            Close
                          </button>
                        </DialogComponent>
                      </div>
                      <div className="my-5">
                        <h4>Method (Optional)</h4>
                        <div className="text-left">
                          <TextEditor
                            content={method}
                            onUpdate={(content) => setMethod(content)}
                          />
                        </div>
                      </div>
                      <div className="my-5">
                        <h4>Extra Info (Optional)</h4>
                        {info.length ? (
                          <>
                            {info.map((i, index) => (
                              <div
                                key={index}
                                className="my-5 flex w-full items-center justify-between"
                                // style={{ width: "400px" }}
                              >
                                <Input
                                  value={i}
                                  width="350px"
                                  autoFocus
                                  onChange={(e) =>
                                    setInfo(
                                      info.map((inf, ind) =>
                                        index == ind ? e.target.value : inf
                                      )
                                    )
                                  }
                                />
                                <button
                                  className=""
                                  onClick={() =>
                                    setInfo(
                                      info.filter((inf, ind) => index != ind)
                                    )
                                  }
                                >
                                  <XCircleIcon className="icon-lg icon-gray" />
                                </button>
                              </div>
                            ))}
                            <button
                              className="btn btn-primary"
                              onClick={() => setInfo([...info, ""])}
                            >
                              Add More Info
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-primary"
                            onClick={() => setInfo([""])}
                          >
                            Add Some Info
                          </button>
                        )}
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
        </div>
        {recipeName &&
        servings &&
        (!user.school?.requiresTaggedRecipes || recipeTags.length) ? (
          isLoading ? (
            <div className="flex w-screen max-w-md flex-col items-center justify-center gap-4">
              <h2>Loading Ingredients</h2>
              <p>Please wait while we fetch the ingredients...</p>
              <div className="spinner" />
            </div>
          ) : (
            <div className="w-screen max-w-md">
              <ListedRecipeIngredients
                {...{
                  user,
                  recipeIngredientSections,
                  setRecipeIngredientSections,
                }}
              />
              <div
                className={classNames(
                  "my-5",
                  recipeIngredientSections.some((ris) =>
                    ris.ingredients.some((i) =>
                      defaultIngredients.some((di) => di.id === i.id)
                    )
                  )
                    ? ""
                    : "rounded border border-solid border-cyan-300 bg-gradient-to-tr from-cyan-50 to-green-50 p-2.5"
                )}
              >
                <span className="mb-2 block font-normal">
                  Select an ingredient to add to the recipe
                </span>
                <AsyncSelectIngredient
                  value={null}
                  externalRef={ingredientInput}
                  autoFocus={false}
                  onChange={(value) => {
                    if (value) {
                      setRecipeIngredientSections((prev) => {
                        console.log("prev", prev);
                        const tempSection = prev[prev.length - 1];

                        console.log("tempSection", tempSection);

                        return [
                          ...prev.slice(0, -1),
                          {
                            ...tempSection,
                            ingredients: [
                              ...tempSection.ingredients,
                              {
                                id: value.id,
                                uuid: cuid(),
                                amount: 1,
                                preparation: "",
                                unitId:
                                  getBaseUnit(
                                    combineUnits(value),
                                    value.selectionUnit ?? undefined
                                  )?.id || "",
                              },
                            ],
                          },
                        ];
                      });
                    }
                  }}
                  onCreated={(ingredients) => {
                    const tempIngredient = ingredients[0];
                    const tempUUID = cuid();

                    setRecipeIngredientSections((prev) => {
                      const tempSection = prev[prev.length - 1];

                      return [
                        ...prev.slice(0, -1),
                        {
                          ...tempSection,
                          ingredients: [
                            ...tempSection.ingredients,
                            {
                              id: tempIngredient.id,
                              uuid: tempUUID,
                              amount: 1,
                              preparation: "",
                              unitId:
                                getBaseUnit(
                                  combineUnits(tempIngredient),
                                  tempIngredient.selectionUnit ?? undefined
                                )?.id || "",
                            },
                          ],
                        },
                      ];
                    });

                    // focus the ingredient amount input
                    setTimeout(() => {
                      const ingredientInput = document.getElementById(
                        "amount" + tempUUID
                      ) as HTMLInputElement;

                      ingredientInput?.focus();
                    }, 750);
                  }}
                  placeholder="Apple..."
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "400px",
                      display: "inline-block",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 20,
                    }),
                  }}
                  className="max-w-full"
                />
                <UserContext.Consumer>
                  {(user) =>
                    isStudent(user) &&
                    !user.school?.permCreateIngredients ? null : (
                      <div
                        className={classNames(
                          isCreatingIngredients
                            ? "my-2.5 rounded border border-solid border-cyan-200 bg-gradient-to-tr from-cyan-50 to-green-50 p-2.5"
                            : ""
                        )}
                      >
                        <button
                          onClick={() =>
                            setCreatingIngredients(!isCreatingIngredients)
                          }
                          className={
                            isCreatingIngredients ? "btn btn-warning" : "btn"
                          }
                        >
                          {isCreatingIngredients
                            ? "Cancel"
                            : "Or Create a New One"}
                        </button>
                        {isCreatingIngredients && user.school ? (
                          <CreateIngredients
                            school={user.school}
                            onCompleted={() => {
                              // remove the create ingredients section
                              setCreatingIngredients(false);

                              if (
                                ingredientInput.current !=
                                document.activeElement
                              ) {
                                ingredientInput.current?.focus();
                              }

                              // Let the user know: created but will take a while to show up in search
                              toast(
                                "The created ingredients may take a few seconds to show up in search results."
                              );
                            }}
                          />
                        ) : null}
                      </div>
                    )
                  }
                </UserContext.Consumer>
              </div>
            </div>
          )
        ) : null}
      </div>
      {recipeName &&
      servings &&
      (!user.school?.requiresTaggedRecipes || recipeTags.length) &&
      getRecipeIngredients(recipeIngredientSections).length ? (
        <CreateRecipes submitRecipe={submitRecipe} />
      ) : null}
    </>
  );
};

export default CreateRecipeManually;
